import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  customsDeclarations,
  type CustomsDeclarationListItem,
} from '@/api/customsDeclarations';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function CustomsDeclarationsPage() {
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ['customs-declarations'],
    queryFn: () => customsDeclarations.list({ limit: 200 }),
  });

  const cols = useMemo<ColumnDef<CustomsDeclarationListItem>[]>(() => [
    { key: 'document_number', header: '№ внутр.', width: 130, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
    { key: 'document_date', header: 'Дата', width: 170, sortValue: (r) => r.document_date ?? '', cell: (r) => r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '' },
    { key: 'customs_number', header: '№ ВМД', sortValue: (r) => r.customs_number ?? '', cell: (r) => r.customs_number },
    { key: 'customs_date', header: 'Дата ВМД', width: 130, sortValue: (r) => r.customs_date ?? '', cell: (r) => r.customs_date ? new Date(r.customs_date).toLocaleDateString('uk-UA') : '' },
    { key: 'total_customs_value', header: 'Митна варт.', width: 140, align: 'right', sortValue: (r) => Number(r.total_customs_value), cell: (r) => fmt2(r.total_customs_value) },
    { key: 'total_cost_addition', header: 'У собівартість', width: 150, align: 'right', sortValue: (r) => Number(r.total_cost_addition), cell: (r) => fmt2(r.total_cost_addition) },
    { key: 'total_vat', header: 'ПДВ імпорт', width: 130, align: 'right', sortValue: (r) => Number(r.total_vat), cell: (r) => fmt2(r.total_vat) },
    { key: 'posted', header: 'Проведено', width: 120, align: 'center', sortValue: (r) => r.posted ? 1 : 0, cell: (r) => r.posted ? '✓ так' : 'ні' },
  ], []);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Митні декларації (ВМД при імпорті)</h2>
        <button className="primary" onClick={() => navigate('/customs-declarations/new')}>
          + Новий документ
        </button>
      </div>
      <DataTable<CustomsDeclarationListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/customs-declarations/${r.id}`)}
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
